import { enablePatches, produceWithPatches, setAutoFreeze } from 'immer'
import { JBAction } from './actions'
import { isEqual } from 'lodash'
import { getInitialState } from './initialState'
import { HistoryReducer } from '../../views/apps/engagement/marketing-automation/journey-builder/core/history'
import { setLocalStorage } from '../../views/apps/engagement/marketing-automation/journey-builder/utils'
import {
  NodeFactory,
  NodeType,
  WorkflowController
} from '../../views/apps/engagement/marketing-automation/journey-builder/core/workflow'
import { autoLayoutWorkflow } from '../../views/apps/engagement/marketing-automation/journey-builder/views/canvas/auto-layout-workflow'
// Enable Immer patches for tracking state changes
enablePatches()
// Disable auto-freezing in Immer to allow mutable state modifications
setAutoFreeze(false)

export class StoreReducer {
  // Instance of the HistoryReducer to manage undo/redo functionality
  historyReducer = new HistoryReducer()

  /**
   * Get the reducer function. This function processes actions and updates the application state.
   * @param {JBState} stateData - The current state data.
   * @param {UnknownAction} action - The action to be processed.
   * @returns {JBState} The new state data after applying the action.
   */
  getReducer = (stateData, action) => {
    const state = stateData ?? getInitialState()

    if (!action?.type) {
      return state
    }

    const [nextState, redoPatches, undoPatches] = produceWithPatches(state, draft => this.reducer(draft, action))
    // Store the action and its patches in the history reducer
    this.historyReducer.storeAction(action, undoPatches, redoPatches)

    // Update the UI history and track diagram changes
    nextState.ui.history = this.historyReducer.getState()

    // Handle diagram changes
    if (action.type === 'CLEAR_DIAGRAM_CHANGES') {
      nextState.changes = null
    } else if (action.type === JBAction.BATCH_WORKFLOW_UPDATE) {
      // Compute diagram changes for validation updates so canvas nodes re-render with updated validation state.
      // BATCH_WORKFLOW_UPDATE only mutates node.ui.validation (replace patches), never resets or zooms.
      const newChanges = this.getDiagramChanges(nextState, state, redoPatches)
      if (newChanges !== null) {
        nextState.changes = this.mergeDiagramChanges(state.changes, newChanges)
      } else {
        nextState.changes = state.changes
      }
    } else if (action.type === JBAction.UPDATE_WORKFLOW_ERROR_STACK) {
      // Skip diagram changes for error stack updates - these don't affect node rendering
      nextState.changes = state.changes
    } else {
      const newChanges = this.getDiagramChanges(nextState, state, redoPatches)
      // Only update changes if there are actual changes, otherwise preserve existing changes
      if (newChanges !== null) {
        nextState.changes = this.mergeDiagramChanges(state.changes, newChanges)
      } else if (state.changes === undefined) {
        // First time setting, initialize to null
        nextState.changes = null
      } else {
        // Preserve existing changes until they are cleared
        nextState.changes = state.changes
      }
    }

    return nextState
  }

  /**
   * Process the action and update the state.
   * This method contains the main logic for handling various action types.
   * @param {Draft<JBState>} state - The current state (mutable draft).
   * @param {UnknownAction} action - The action to be processed.
   * @returns {Draft<JBState>} The updated state after processing the action.
   */
  reducer(state, action) {
    if (state === undefined) {
      state = getInitialState()
    }

    switch (action.type) {
      case 'SET_EXTERNAL_STATE': {
        state.workflow.nodes = action.payload.workflow.nodes
        state.workflow.links = action.payload.workflow.links
        state.ui = action.payload.ui
        break
      }

      case 'SET_PALETTE_NODES': {
        const nodes = action.payload
        const nodeMap = {}
        if (Array.isArray(nodes)) {
          for (const node of nodes) {
            nodeMap[`${node.type}|${node.subtype}`] = node
          }
        }
        state.ui.palette.nodes = nodes
        state.ui.palette.nodeMap = nodeMap
        break
      }

      case JBAction.BATCH_WORKFLOW_UPDATE: {
        const payload = action.payload
        if (payload) {
          if (payload.nodes) {
            // Update individual nodes to avoid triggering a full diagram reset in #getDiagramChanges
            Object.keys(payload.nodes).forEach(id => {
              if (state.workflow.nodes[id]) {
                if (!isEqual(state.workflow.nodes[id], payload.nodes[id])) {
                  state.workflow.nodes[id] = payload.nodes[id]
                }
              } else {
                state.workflow.nodes[id] = payload.nodes[id]
              }
            })
            // Remove nodes that are no longer in payload.nodes
            Object.keys(state.workflow.nodes).forEach(id => {
              if (!payload.nodes[id]) {
                delete state.workflow.nodes[id]
              }
            })
          }
          if (payload.links) {
            Object.keys(payload.links).forEach(id => {
              if (state.workflow.links[id]) {
                if (!isEqual(state.workflow.links[id], payload.links[id])) {
                  state.workflow.links[id] = payload.links[id]
                }
              } else {
                state.workflow.links[id] = payload.links[id]
              }
            })
            // Remove links that are no longer in payload.links
            Object.keys(state.workflow.links).forEach(id => {
              if (!payload.links[id]) {
                delete state.workflow.links[id]
              }
            })
          }
        }
        break
      }

      case JBAction.UPDATE_WORKFLOW_ERROR_STACK: {
        const payload = action.payload
        if (payload) {
          state.ui.errorStack = payload.errors ? payload.errors : state.ui.errorStack
          state.ui.errorStack.count = payload.count
        }
        break
      }

      case JBAction.LOAD_WORKFLOW: {
        const payload = action.payload
        if (payload?.workflow?.definition?.nodes && payload?.workflow?.definition?.edges) {
          // Reset lastChipIndex before loading new workflow data so indexing starts fresh
          state.workflow.lastChipIndex = 0
          WorkflowController.addDataFromRaw(payload.workflow.definition, state.workflow)
          state.workflow.name = payload.workflow.name
          state.workflow.journeyId = payload.journey?.id || ''
        }
        break
      }

      case JBAction.EXIT_WORKFLOW: {
        const initial = getInitialState()
        state.workflow = initial.workflow
        state.ui = initial.ui
        state.changes = initial.changes
        state.mode = initial.mode
        break
      }

      case JBAction.MOCK_MODE_TOGGLE: {
        state.ui.mockMode = action.payload
        state.ui.zoomScale = 0.0
        break
      }

      case JBAction.SET_JOURNEY_PAUSED: {
        state.mode = action.payload ? 'paused' : 'edit'
        break
      }

      case JBAction.SET_MODE: {
        state.mode = action.payload
        break
      }

      case JBAction.CLEAR_WORKFLOW: {
        state.workflow = { journeyId: '', nodes: {}, links: {}, name: '', lastChipIndex: 0 }
        state.ui.selection = { nodes: [], links: [] }
        state.ui.propertiesPanel = { expanded: false }
        state.ui.contextMenu = {
          open: false,
          elementType: null,
          uiPosition: null,
          canvasPosition: null
        }
        state.ui.errorStack = {}
        // Reset history to prevent undo from restoring old lastChipIndex
        this.historyReducer = new HistoryReducer()
        break
      }

      case JBAction.ZOOM_IN: {
        state.ui.zoomScale = state.ui.zoomScale + 0.1
        break
      }

      case JBAction.ZOOM_OUT: {
        state.ui.zoomScale = state.ui.zoomScale - 0.1
        break
      }

      case JBAction.ZOOM_TO_FIT: {
        state.ui.zoomScale = 'auto'
        break
      }

      case JBAction.ZOOM_SET: {
        state.ui.zoomScale = action.payload
        break
      }

      case JBAction.AUTO_LAYOUT_WORKFLOW: {
        autoLayoutWorkflow(state.workflow)
        break
      }

      case JBAction.PALETTE_SEARCH_TERM_CHANGE: {
        state.ui.palette.searchTerm = action.payload
        setLocalStorage('palette', state.ui.palette)
        break
      }

      case JBAction.PALETTE_VISIBILITY_CHANGE: {
        state.ui.palette.expanded = !state.ui.palette.expanded
        setLocalStorage('palette', state.ui.palette)
        break
      }

      case JBAction.PROPERTIES_PANEL_OPEN: {
        state.ui.propertiesPanel = { expanded: true }
        break
      }

      case JBAction.PROPERTIES_PANEL_CLOSE: {
        state.ui.propertiesPanel = { expanded: false }
        break
      }

      case JBAction.CONTEXT_MENU_OPEN: {
        state.ui.contextMenu = { ...action.payload, open: true }
        break
      }

      case JBAction.CONTEXT_MENU_CLOSE: {
        state.ui.contextMenu = {
          open: false,
          elementType: null,
          uiPosition: null,
          canvasPosition: null
        }
        break
      }

      case JBAction.BLANK_CLICK: {
        state.ui.selection = { nodes: [], links: [] }
        state.ui.propertiesPanel = { expanded: false }
        state.ui.errorNode = { open: false, nodeId: '', errors: [], label: '', target: null }
        break
      }

      case JBAction.NODE_SELECTED: {
        state.ui.selection = { nodes: [action.payload], links: [] }
        state.ui.propertiesPanel = { expanded: true }
        break
      }

      case JBAction.LINK_SELECTED: {
        state.ui.selection = { nodes: [], links: [action.payload] }
        state.ui.propertiesPanel = { expanded: false }
        break
      }

      case JBAction.NODE_ERROR_CLICK: {
        const { nodeId, target } = action.payload
        const node = state.workflow.nodes[nodeId]
        if (node && node.ui?.validation?.errors?.length > 0) {
          // Open new popover after cleanup
          state.ui.errorNode = {
            open: true,
            nodeId: node.node_uid,
            errors: node.ui.validation.errors,
            label: node.ui.label || node.data?.label || 'Node',
            target
          }
        } else {
          state.ui.errorNode = { open: false, nodeId: '', errors: [], label: '' }
        }
        break
      }

      case JBAction.HALO_SEARCH_FILTER: {
        const { searchTerm, source } = action.payload

        // Ensure halo state exists before using it
        if (!state.ui.halo) {
          state.ui.halo = { compatibleNodes: null, searchTerm: '', source: null }
        }

        const filteredNodes = WorkflowController.searchHaloNodes(
          searchTerm,
          source,
          state.ui.palette.nodes,
          state.workflow
        )
        state.ui.halo = {
          ...state.ui.halo,
          compatibleNodes: filteredNodes,
          searchTerm,
          source
        }
        break
      }

      case JBAction.INFO_PANEL_OPEN: {
        const { nodeId, target } = action.payload
        const node = state.workflow.nodes[nodeId]
        if (node) {
          state.ui.infoPanel = {
            open: true,
            nodeId,
            target
          }
        } else {
          state.ui.infoPanel = { open: false, nodeId: '', target: '' }
        }
        break
      }

      case JBAction.INFO_PANEL_CLOSE: {
        state.ui.infoPanel = { open: false, nodeId: '', target: '' }
        break
      }

      case JBAction.SCROLL_TO_NODE: {
        state.ui.scrollToNodeId = action.payload
        break
      }
      case JBAction.CLEAR_SCROLL_TO_NODE: {
        state.ui.scrollToNodeId = null
        break
      }

      case JBAction.NODE_ERROR_CLEAR: {
        state.ui.errorNode = { open: false, nodeId: '', errors: [], label: '', target: null }
        break
      }

      case JBAction.CELLS_SELECTED: {
        state.ui.selection = action.payload
        state.ui.propertiesPanel = { expanded: false }
        break
      }

      case JBAction.NODES_MOVED: {
        WorkflowController.updateNodesPosition(action.payload, state.workflow)
        break
      }

      case JBAction.NODE_DROPPED_FROM_PALETTE: {
        const node = action.payload

        if (node.type === NodeType.START || node.type === 'start') {
          const alreadyHasStart = Object.values(state.workflow.nodes || {}).some(
            n => n.type === NodeType.START || n.type === 'start'
          )
          if (alreadyHasStart) {
            break
          }
        }

        WorkflowController.addNewNode(
          node.type,
          node.position,
          state.workflow,
          null,
          node.nodeData,
          state.ui.palette.nodes
        )
        break
      }

      case JBAction.NODE_LABEL_CHANGED: {
        const { nodeId, label } = action.payload
        WorkflowController.updateNodeLabel(nodeId, label, state.workflow)
        break
      }

      case JBAction.NODE_DESCRIPTION_CHANGED: {
        const { nodeId, description } = action.payload
        WorkflowController.updateNodeDescription(nodeId, description, state.workflow)
        break
      }

      case JBAction.NODE_DATA_CHANGED: {
        const { nodeId, data } = action.payload
        WorkflowController.updateNodeProperties(nodeId, data, state.workflow)
        break
      }

      case JBAction.NODE_FORMDATA_CHANGED: {
        const { nodeId, data } = action.payload
        console.log('Redux Reducer: NODE_FORMDATA_CHANGED: nodeId: ', nodeId, 'data: ', data)
        if (state.workflow.nodes[nodeId]) {
          state.workflow.nodes[nodeId].data.form = data

          // Sync case-specific fields from form data back to node.data
          // so WorkflowController can manage edges correctly
          const nodeType = state.workflow.nodes[nodeId].type
          if (nodeType === 'case' || nodeType === NodeType.CASE) {
            const casesVal = data.cases !== undefined ? data.cases : data.data?.cases
            if (casesVal !== undefined) {
              state.workflow.nodes[nodeId].data.cases = casesVal
            }
            const defaultTargetVal = data.default_target !== undefined ? data.default_target : data.data?.default_target
            if (defaultTargetVal !== undefined) {
              state.workflow.nodes[nodeId].data.default_target = defaultTargetVal
            }
            const attributeVal = data.attribute !== undefined ? data.attribute : data.data?.attribute
            if (attributeVal !== undefined) {
              state.workflow.nodes[nodeId].data.attribute = attributeVal
            }
          }
          // Sync if-else specific fields
          if (nodeType === 'if-else' || nodeType === NodeType.IF_ELSE) {
            const conditionsVal = data.conditions !== undefined ? data.conditions : data.data?.conditions
            if (conditionsVal !== undefined) {
              state.workflow.nodes[nodeId].data.conditions = conditionsVal
            }
            const elseTargetVal = data.else_target !== undefined ? data.else_target : data.data?.else_target
            if (elseTargetVal !== undefined) {
              state.workflow.nodes[nodeId].data.else_target = elseTargetVal
            }
          }
        }
        break
      }

      case JBAction.NODE_VALIDATION_CHANGED: {
        const { nodeId, data } = action.payload
        WorkflowController.updateNodeValidationProperties(nodeId, data, state.workflow)
        break
      }

      case JBAction.NODE_VALIDATE: {
        // WorkflowController.validateNode(nodeId, state.workflow)
        break
      }

      case JBAction.BATCH_NODE_VALIDATION_UPDATE: {
        const { data } = action.payload
        WorkflowController.batchUpdateNodeValidationProperties(data, state.workflow)
        break
      }

      case JBAction.NODE_NEXT_CONNECTION_CREATED: {
        const { nodeId, position, newNodeType } = action.payload

        // Find matching node from palette sections
        let matchingNode = null

        if (state.ui?.palette?.nodes) {
          matchingNode = state.ui?.palette?.nodes.find(node => node.type === newNodeType)
        }

        WorkflowController.createNodeNextConnection(nodeId, position, newNodeType, state.workflow, matchingNode || {})
        break
      }

      case JBAction.NODE_COPIED: {
        const { nodeId, position } = action.payload
        if (!state.workflow.nodes[nodeId]) {
          break
        }
        WorkflowController.cloneNode(nodeId, position, state.workflow)
        break
      }

      case JBAction.NODE_LINKS_REMOVED: {
        WorkflowController.removeNodeLinks(action.payload, state.workflow)
        this.clearUIWhenRemoveElement(state)
        break
      }

      case JBAction.NODE_REMOVED: {
        WorkflowController.removeNode(action.payload, state.workflow, action.meta?.removeCaseCondition)
        this.clearUIWhenRemoveElement(state)
        break
      }
      // Node replace
      case JBAction.NODE_REPLACE: {
        const { nodeId, newNodeType } = action.payload
        if (!state.workflow.nodes[nodeId]) {
          break
        }

        WorkflowController.replaceNode(nodeId, newNodeType, state.workflow, state.ui?.palette?.nodes)
        // this.#clearUIWhenRemoveElement(state);
        break
      }

      case JBAction.LINK_CREATED: {
        WorkflowController.addNewLink(action.payload, state.workflow)
        break
      }

      case JBAction.VALIDATE_CONNECTION_LIMIT: {
        const { nodeId } = action.payload
        WorkflowController.evaluateNodeConnectionLimits(nodeId, state.workflow)
        break
      }

      case JBAction.LINK_TARGET_REPLACED: {
        const { linkId, target, source, linkView } = action.payload
        WorkflowController.replaceLinkTarget(linkId, source, target, state.workflow, linkView)
        break
      }

      case JBAction.LINK_LABEL_CHANGED: {
        const { linkId, label } = action.payload
        WorkflowController.updateLinkLabel(linkId, label, state.workflow)
        break
      }

      case JBAction.LINK_REMOVED: {
        WorkflowController.removeLink(action.payload, state.workflow, action.meta?.removeCaseCondition ?? true)
        this.clearUIWhenRemoveElement(state)
        break
      }

      case JBAction.COPY_SELECTION: {
        state.ui.clipboard = WorkflowController.createClipboardFromSelection(state.ui.selection, state.workflow)
        break
      }

      case JBAction.CUT_SELECTION: {
        // copy to clipboard
        state.ui.clipboard = WorkflowController.createClipboardFromSelection(state.ui.selection, state.workflow)
        // remove selected shapes (nodes and links)
        WorkflowController.removeSelection(state.ui.selection, state.workflow, action.meta?.removeCaseCondition)
        this.clearUIWhenRemoveElement(state)
        break
      }

      case JBAction.REMOVE_SELECTION: {
        WorkflowController.removeSelection(state.ui.selection, state.workflow, action.meta?.removeCaseCondition)
        this.clearUIWhenRemoveElement(state)
        break
      }

      case JBAction.PASTE: {
        if (!state.ui.clipboard) {
          break
        }
        const { links, nodes } = WorkflowController.pasteFromClipboard(
          state.ui.clipboard,
          action.payload,
          state.workflow
        )
        state.ui.selection = {
          nodes: nodes.map(n => n.node_uid),
          links: links.map(l => l.edge_uid)
        }
        break
      }

      case JBAction.UNDO: {
        this.historyReducer.undo(state)
        break
      }

      case JBAction.REDO: {
        this.historyReducer.redo(state)
        break
      }

      case 'CLEAR_DIAGRAM_CHANGES': {
        // This action will cause diagramChanges to be set to null
        break
      }
    }
    return state
  }

  /**
   * Get the changes made to the diagram based on the redo patches.
   *
   * @param {JBState} state - The current state.
   * @param {JBState} previousState - The previous state.
   * @param {Patch[]} redoPatches - The patches applied to redo the changes.
   * @returns {ChangesState|null} An object describing the changes or null if no changes occurred.
   */
  getDiagramChanges(state, previousState, redoPatches) {
    const result = {
      reset: false,
      added: { nodes: [], links: [] },
      updated: { nodes: [], links: [] },
      removed: { nodes: [], links: [] }
    }

    const processed = {}
    const { nodes, links } = state.workflow
    const nodesPath = ['workflow', 'nodes']
    const linksPath = ['workflow', 'links']

    for (const patch of redoPatches) {
      const path = patch.path.map(p => p.toString())

      // Handle workflow reset
      if (
        nodesPath.toString() === path.toString() ||
        path.toString() === linksPath.toString() ||
        path.toString() === ['workflow'].toString()
      ) {
        result.reset = true
        result.added.nodes = Object.values(nodes).map(NodeFactory.createCanvasNode)
        result.added.links = Object.values(links)
        return result
      }

      const isNodePatch = nodesPath.every((pathSegment, idx) => pathSegment === path[idx])
      const isLinkPatch = linksPath.every((pathSegment, idx) => pathSegment === path[idx])

      // Skip non-workflow patches but log them
      if (!isNodePatch && !isLinkPatch) {
        continue
      }

      switch (patch.op) {
        case 'add': {
          const id = path[path.length - 1]
          if (id && isNodePatch && !processed[id] && nodes[id]) {
            processed[id] = true
            const canvasNode = NodeFactory.createCanvasNode(nodes[id])
            result.added.nodes.push(canvasNode)
          }
          if (id && isLinkPatch && !processed[id] && links[id]) {
            processed[id] = true
            result.added.links.push(links[id])
          }
          break
        }

        case 'remove': {
          const id = path[path.length - 1]
          if (id && isNodePatch && !processed[id]) {
            processed[id] = true
            result.removed.nodes.push(id)
          }
          if (id && isLinkPatch && !processed[id]) {
            processed[id] = true
            result.removed.links.push(id)
          }
          break
        }

        case 'replace': {
          if (isNodePatch) {
            const id = path[nodesPath.length]
            if (id && !processed[id] && nodes[id]) {
              processed[id] = true
              const canvasNode = NodeFactory.createCanvasNode(nodes[id])
              result.updated.nodes.push(canvasNode)
            }
          }

          if (isLinkPatch) {
            const id = path[linksPath.length]
            if (id && !processed[id] && links[id]) {
              processed[id] = true
              result.updated.links.push(links[id])
            }
          }
          break
        }
      }
    }

    const hasChanges =
      result.added.nodes.length ||
      result.added.links.length ||
      result.removed.nodes.length ||
      result.removed.links.length ||
      result.updated.nodes.length ||
      result.updated.links.length

    return hasChanges ? result : null
  }

  mergeDiagramChanges(existing, newChanges) {
    if (!existing) {
      return newChanges
    }
    if (!newChanges) {
      return existing
    }
    if (newChanges.reset) {
      return newChanges
    }
    if (existing.reset) {
      return existing
    }

    const merged = {
      reset: false,
      added: {
        nodes: [...(existing.added?.nodes || []), ...(newChanges.added?.nodes || [])],
        links: [...(existing.added?.links || []), ...(newChanges.added?.links || [])]
      },
      updated: {
        nodes: [...(existing.updated?.nodes || []), ...(newChanges.updated?.nodes || [])],
        links: [...(existing.updated?.links || []), ...(newChanges.updated?.links || [])]
      },
      removed: {
        nodes: [...(existing.removed?.nodes || []), ...(newChanges.removed?.nodes || [])],
        links: [...(existing.removed?.links || []), ...(newChanges.removed?.links || [])]
      }
    }

    const uniqueNodes = nodes => {
      const seen = new Set()
      return nodes.filter(n => {
        const id = typeof n === 'string' ? n : n.id
        if (seen.has(id)) {
          return false
        }
        seen.add(id)
        return true
      })
    }

    const uniqueLinks = links => {
      const seen = new Set()
      return links.filter(l => {
        const id = typeof l === 'string' ? l : l.edge_uid
        if (seen.has(id)) {
          return false
        }
        seen.add(id)
        return true
      })
    }

    merged.added.nodes = uniqueNodes(merged.added.nodes)
    merged.added.links = uniqueLinks(merged.added.links)
    merged.updated.nodes = uniqueNodes(merged.updated.nodes)
    merged.updated.links = uniqueLinks(merged.updated.links)
    merged.removed.nodes = uniqueNodes(merged.removed.nodes)
    merged.removed.links = uniqueLinks(merged.removed.links)

    return merged
  }

  clearUIWhenRemoveElement(state) {
    state.ui.selection = { nodes: [], links: [] }
    state.ui.propertiesPanel = { expanded: false }
    state.ui.contextMenu = {
      open: false,
      elementType: null,
      uiPosition: null,
      canvasPosition: null
    }
  }
}
